<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import General from "@/services/General";

export default {
  components: { Layout, PageHeader },
  page: {
    title: "API Changelog",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },
  data() {
    return {
      title: "API Changelog",
      items: [
        {
          text: "Changelog",
        },
        {
          text: "API Changelog",
          active: true,
        },
      ],
      tableData: [],
      error: null,
      isBusy: false,
      currentPage: 1,
      perPage: 10,
      pageOptions: [10, 25, 50, 100],
      filter: null,
      filterOn: [],
      sortBy: "commit_timestamp",
      sortDesc: true,
      totalRows: 0,
      fields: [
        {
          key: "id",
          label: "ID",
          sortable: true,
          class: "text-center",
          thStyle: { width: "5%" },
        },
        {
          key: "commit_hash",
          label: "Commit Hash",
          sortable: false,
          thStyle: { width: "12%" },
        },
        {
          key: "author_name",
          label: "Author",
          sortable: true,
          thStyle: { width: "10%" },
        },
        {
          key: "author_email",
          label: "Email",
          sortable: true,
          thStyle: { width: "12%" },
        },
        {
          key: "commit_message",
          label: "Commit Message",
          sortable: false,
          thStyle: { width: "25%" },
        },
        {
          key: "branch",
          label: "Branch",
          sortable: false,
          thStyle: { width: "8%" },
        },
        {
          key: "commit_timestamp",
          label: "Commit Date",
          sortable: true,
          thStyle: { width: "12%" },
        },
        {
          key: "processed_at",
          label: "Processed At",
          sortable: true,
          thStyle: { width: "12%" },
        },
      ],
    };
  },
  async mounted() {
    await this.loadApiChangelog();
  },
  methods: {
    async loadApiChangelog() {
      this.isBusy = true;
      this.error = null;
      try {
        const response = await General.getApiChangelog();
        if (response.data && response.data.data) {
          this.tableData = response.data.data;
          this.totalRows = this.tableData.length;
        } else if (response.data && response.data.error) {
          this.error = response.data.error;
          this.tableData = [];
          this.totalRows = 0;
        }
      } catch (err) {
        this.error = err.response?.data?.error || err.message || "Failed to load API changelog";
        this.tableData = [];
        this.totalRows = 0;
        console.error("Error loading API changelog:", err);
      } finally {
        this.isBusy = false;
      }
    },
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
    formatDate(dateString) {
      if (!dateString) return "N/A";
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch (e) {
        return dateString;
      }
    },
    formatCommitHash(hash) {
      if (!hash) return "N/A";
      if (hash.length <= 12) return hash;
      const firstPart = hash.substring(0, 6);
      const lastPart = hash.substring(hash.length - 6);
      return `${firstPart}.......${lastPart}`;
    }
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <!-- Error Message -->
            <div v-if="error" class="alert alert-warning mb-4" role="alert">
              <h6 class="alert-heading">
                <i class="uil uil-exclamation-triangle me-2"></i>
                Unable to load API changelog
              </h6>
              <p class="mb-0">{{ error }}</p>
            </div>

            <!-- Search Filter -->
            <div class="row mt-4" v-if="!error">
              <div class="col-sm-12 col-md-3">
                <div class="dataTables_length">
                  <label class="d-inline-flex align-items-center">
                    Show&nbsp;
                    <b-form-select
                      v-model="perPage"
                      size="sm"
                      :options="pageOptions"
                    ></b-form-select>
                    &nbsp;entries
                  </label>
                </div>
              </div>

              <!-- Search -->
              <div class="col-sm-12 col-md-9">
                <div
                  id="api-changelog-table_filter"
                  class="dataTables_filter text-md-end"
                >
                  <label class="d-inline-flex align-items-center">
                    Search:
                    <b-form-input
                      v-model="filter"
                      type="search"
                      placeholder="Search..."
                      class="form-control form-control-sm ms-2"
                    ></b-form-input>
                  </label>
                </div>
              </div>
              <!-- End search -->
            </div>

            <!-- Table -->
            <div class="table-responsive mb-0" v-if="!error">
              <b-table
                :busy="isBusy"
                :items="tableData"
                :fields="fields"
                responsive="sm"
                :per-page="perPage"
                :current-page="currentPage"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                :filter="filter"
                :filter-included-fields="filterOn"
                show-empty
                empty-text="No API changelog entries found"
                @filtered="onFiltered"
              >
                <!-- Loading slot -->
                <template v-slot:table-busy>
                  <div class="text-center text-primary my-2">
                    <b-spinner class="align-middle"></b-spinner>
                    <strong> Loading...</strong>
                  </div>
                </template>

                <!-- Commit Hash -->
                <template v-slot:cell(commit_hash)="data">
                  <code class="text-primary">{{ formatCommitHash(data.value) }}</code>
                </template>

                <!-- Commit Message -->
                <template v-slot:cell(commit_message)="data">
                  <span class="text-truncate d-inline-block" style="max-width: 400px;" :title="data.value">
                    {{ data.value || "N/A" }}
                  </span>
                </template>

                <!-- Commit Timestamp -->
                <template v-slot:cell(commit_timestamp)="data">
                  {{ formatDate(data.value) }}
                </template>

                <!-- Processed At -->
                <template v-slot:cell(processed_at)="data">
                  {{ formatDate(data.value) }}
                </template>

                <!-- Branch -->
                <template v-slot:cell(branch)="data">
                  {{ data.value || "N/A" }}
                </template>
              </b-table>
            </div>

            <!-- Pagination -->
            <div class="row" v-if="!error && totalRows > 0">
              <div class="col">
                <div class="dataTables_paginate paging_simple_numbers float-end">
                  <ul class="pagination pagination-rounded">
                    <b-pagination
                      v-model="currentPage"
                      :total-rows="totalRows"
                      :per-page="perPage"
                      class="my-0"
                    ></b-pagination>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>